


import { useState } from 'react';
import {  appStateActions, ResponseHook, AppService, i18n, DEFAULT_LANG, sessionStateActions, ActionResponse } from '@/Shared';
import {  useAppDispatch, useAppSelector } from '@/OneLiners';
import { AuthService } from "../AuthService";

/**
 * =====================================================
 * Hook: useSession
 * =====================================================
 *
 * Description:
 * Custom hook to manage application session state and related initialization logic.
 * Handles enums, translations, user session, and session validation.
 *
 * Responsibilities:
 * - Clear or reset user session
 * - Initialize enums from backend
 * - Check current session status
 * - Initialize full session including translations and enums
 * - Provide reactive ActionResponse states for each async operation
 *
 * Returns:
 * - checkSession(): verifies current user session
 * - initEnums(): fetches backend enums and stores them in Redux
 * - initSession(): initializes full session (user session, enums, translations)
 * - clearSession(): resets user session
 * - initEnumsResponse: ActionResponse of enum initialization
 * - checkSessionResponse: ActionResponse of session check
 * - sessionChecked: current session validation result
 * - initSessionResponse: ActionResponse of full session initialization
 * - clearSessionResponse: ActionResponse of clearing session
 *
 * Notes:
 * - ResponseHook helpers manage loading, success, and error states
 * - initTranslation ensures translations are loaded and cached
 *
 * Author: Samano CASTRE
 * Date: February 1, 2026
 */
export const useSession = () => {

	/** =====================================================
     * Redux & Local State
     * =====================================================
     *
     * Retrieves current translation state from Redux and
     * manages ActionResponse states for session-related operations.
     */
	const { translation } = useAppSelector(state => state.appState);
	const dispatch = useAppDispatch();

	const [initEnumsResponse, setInitEnumsResponse] = useState<ActionResponse>({ status: 'idle' });
	const [checkSessionResponse, setCheckSessionResponse] = useState<ActionResponse>({ status: 'idle' });
	const [initSessionResponse, setInitSessionResponse] = useState<ActionResponse>({ status: 'idle' });
	const [initTranslationResponse, setInitTranslationResponse] = useState<ActionResponse>({ status: 'idle' });
	const [clearSessionResponse, setClearSessionResponse] = useState<ActionResponse>({ status: 'idle' });
	const [sessionChecked, setSessionChecked] = useState<{ authenticated: boolean }>();


	/** =====================================================
     * Clear Session
     * =====================================================
     *
     * Resets the user session in Redux and sets the response state.
     */
	const clearSession = async () => {
		ResponseHook.loading(setClearSessionResponse);
		dispatch(sessionStateActions.resetUserSession());
		return ResponseHook.success(setClearSessionResponse);
	}

	/** =====================================================
     * Initialize Enums
     * =====================================================
     *
     * Fetches enum configurations from backend and stores
     * them in Redux. Manages response state.
     */
	const initEnums = async () => {
        try {
            ResponseHook.loading(setInitEnumsResponse);
            const data = await AppService.fetchEnums();

            ResponseHook.success(setInitEnumsResponse);
            const enums = data.data;

            dispatch(appStateActions.setEnums(enums));
            return enums;
        }
        catch (error: any) {
            ResponseHook.error(setInitEnumsResponse, error, "Erreur lors de récupération des configs.");
            return null;
        };
    }

	/** =====================================================
     * Check Session
     * =====================================================
     *
     * Verifies current user session status with AuthService
     * and updates local sessionChecked state.
     */
	const checkSession = async () => {
		try {
			ResponseHook.loading(setCheckSessionResponse);
			const response = await AuthService.checkSession();

			ResponseHook.success(setCheckSessionResponse);
			setSessionChecked(response.data);

			return response.data;
		}
		catch (error: any) {
			ResponseHook.error(setCheckSessionResponse, error, "Erreur lors de la vérification de la session utilisateur.");
			return null;
		}
	};


	/** =====================================================
     * Initialize Full Session
     * =====================================================
     *
     * Retrieves current user session, initializes translations
     * and enums, and stores user session in Redux.
     */
	const initSession = async () => {
		try {
			ResponseHook.loading(setInitSessionResponse);
			const data = await AuthService.getCurrentSession();
			const userSession = data.data;

			// Initialize translations and enums after session retrieval
			await initTranslation(!!userSession.id)
			await initEnums();

			ResponseHook.success(setInitSessionResponse);
			
			// Store session in Redux
			dispatch(sessionStateActions.setUserSession(userSession));
			return userSession;
		}
		catch (error: any) {
			ResponseHook.error(setInitSessionResponse, error, "Erreur lors de la récupération de la session utilisateur.");
			return null;
		}
	};

	/** =====================================================
     * Initialize Translations
     * =====================================================
     *
     * Loads translation data from cache or backend and updates Redux.
     * @param isOnline whether user is currently online
     */
	const initTranslation = async (isOnline: boolean) => {
		try {
			ResponseHook.loading(setInitTranslationResponse);
			let newTranslation = translation ? i18n.getI18nFromCache(translation, isOnline) : null;
			
			if (!newTranslation) {
                const lang = translation?.lang || DEFAULT_LANG;
                const translationResponse = await AppService.fetchI18n(lang, isOnline);

                const translationKeyPair = translationResponse.data as Record<string, string>;
                newTranslation = i18n.updateI18n(translationKeyPair, isOnline, lang);

				dispatch(appStateActions.setTranslation(newTranslation));
            }
			ResponseHook.success(setInitTranslationResponse);
			
			const data = await AuthService.getCurrentSession();
			return newTranslation;
		}
		catch (error: any) {
			ResponseHook.error(setInitTranslationResponse, error, "Erreur lors de la récupération des traductions.");
			return null;
		}
	};

	/** =====================================================
     * Return API
     * =====================================================
     *
     * Exposes session management functions and their
     * corresponding response states.
     */
	return {
		checkSession,
		initEnums,
		initSession,
		clearSession,
		initEnumsResponse,
		checkSessionResponse,
		sessionChecked,
		initSessionResponse,
		clearSessionResponse,
	};
}